import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ExternalLink, Award, Globe, Users, Building } from "lucide-react";
import { Button } from "@/components/ui/button";

const partners = [
  {
    name: "Sotheby's International Realty",
    shortName: "Sotheby's",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/79/Sotheby%27s_logo.svg/200px-Sotheby%27s_logo.svg.png",
    website: "https://www.sothebysrealty.com",
    description: "With a legacy spanning nearly three centuries, Sotheby's International Realty represents the world's most exceptional properties. Their unparalleled global network and commitment to excellence has made them the definitive choice for discerning buyers seeking extraordinary homes.",
    founded: "1744",
    locations: "1,000+",
    countries: "80+",
    specialty: "Ultra-luxury estates, historic properties, and exceptional waterfront homes",
    color: "from-[#1a1a2e] to-[#16213e]"
  },
  {
    name: "Christie's International Real Estate",
    shortName: "Christie's",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d5/Christie%27s_Logo.svg/200px-Christie%27s_Logo.svg.png",
    website: "https://www.christiesrealestate.com",
    description: "Leveraging the prestige of the world's leading auction house, Christie's International Real Estate connects discerning clients with exceptional properties across the globe. Their curated approach ensures each property meets the highest standards of luxury and distinction.",
    founded: "1766",
    locations: "900+",
    countries: "50+",
    specialty: "Art-filled residences, vineyard estates, and architectural masterpieces",
    color: "from-[#2d132c] to-[#4a1942]"
  },
  {
    name: "Knight Frank",
    shortName: "Knight Frank",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a5/Knight_Frank_logo.svg/200px-Knight_Frank_logo.svg.png",
    website: "https://www.knightfrank.com",
    description: "One of the world's largest independent property consultancies, Knight Frank combines local expertise with global reach. Their private office provides bespoke services to ultra-high-net-worth individuals seeking exceptional properties and investment opportunities.",
    founded: "1896",
    locations: "600+",
    countries: "55+",
    specialty: "Country estates, prime London properties, and global investment opportunities",
    color: "from-[#1e3a5f] to-[#0d2137]"
  },
  {
    name: "Savills",
    shortName: "Savills",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c5/Savills_logo.svg/200px-Savills_logo.svg.png",
    website: "https://www.savills.com",
    description: "A global real estate services provider with a reputation built over 160 years, Savills offers comprehensive property expertise across residential, commercial, and rural markets. Their private client advisory team specializes in complex, high-value transactions.",
    founded: "1855",
    locations: "700+",
    countries: "60+",
    specialty: "Prime residential, development sites, and rural estates",
    color: "from-[#0f4c3a] to-[#1a2f2a]"
  }
];

export default function Partners() {
  return (
    <>
      <Helmet>
        <title>Partner Agencies | Agota Estate - Global Luxury Real Estate Network</title>
        <meta name="description" content="Agota Estate collaborates with the world's most prestigious real estate agencies including Sotheby's, Christie's, Knight Frank, and Savills to bring you exceptional properties." />
      </Helmet>
      
      <Navbar />
      
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal to-background" />
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-96 h-96 bg-gold/20 rounded-full blur-3xl" />
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
          </div>
          
          <div className="container mx-auto px-6 lg:px-12 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <span className="inline-block font-body text-gold uppercase tracking-[0.3em] text-sm mb-6">
                Our Global Network
              </span>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-cream mb-6">
                Partner Agencies
              </h1>
              <p className="font-body text-cream/70 text-lg md:text-xl leading-relaxed max-w-3xl mx-auto">
                Agota Estate collaborates with the world's most prestigious real estate agencies, 
                combining local expertise with unparalleled global reach to connect discerning clients 
                with extraordinary properties.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="py-12 bg-gold/5 border-y border-gold/10">
          <div className="container mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { icon: Building, label: "Combined Offices", value: "3,200+" },
                { icon: Globe, label: "Countries", value: "80+" },
                { icon: Users, label: "Agents Worldwide", value: "50,000+" },
                { icon: Award, label: "Years of Excellence", value: "250+" },
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <stat.icon className="w-8 h-8 text-gold mx-auto mb-3" />
                  <p className="font-display text-3xl md:text-4xl text-foreground mb-1">{stat.value}</p>
                  <p className="font-body text-muted-foreground text-sm uppercase tracking-wider">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Partners Grid */}
        <section className="py-20 md:py-28">
          <div className="container mx-auto px-6 lg:px-12">
            <div className="space-y-16 md:space-y-24">
              {partners.map((partner, index) => (
                <motion.article
                  key={partner.name}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.7 }}
                  className={`relative rounded-2xl overflow-hidden ${index % 2 === 1 ? "md:flex-row-reverse" : ""}`}
                >
                  <div className={`bg-gradient-to-br ${partner.color} p-8 md:p-12 lg:p-16`}>
                    <div className={`flex flex-col ${index % 2 === 1 ? "md:flex-row-reverse" : "md:flex-row"} gap-8 md:gap-12 items-center`}>
                      {/* Logo Section */}
                      <div className="flex-shrink-0 w-full md:w-1/3">
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 flex items-center justify-center min-h-[200px]">
                          <img
                            src={partner.logo}
                            alt={`${partner.name} logo`}
                            className="max-w-[180px] max-h-[80px] object-contain filter brightness-0 invert opacity-90"
                            onError={(e) => {
                              // Fallback to text if image fails
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.parentElement!.innerHTML = `<span class="font-display text-2xl text-cream">${partner.shortName}</span>`;
                            }}
                          />
                        </div>
                        
                        {/* Quick Stats */}
                        <div className="grid grid-cols-3 gap-4 mt-6">
                          <div className="text-center">
                            <p className="font-display text-xl text-gold">{partner.founded}</p>
                            <p className="font-body text-cream/60 text-xs uppercase tracking-wider">Founded</p>
                          </div>
                          <div className="text-center">
                            <p className="font-display text-xl text-gold">{partner.locations}</p>
                            <p className="font-body text-cream/60 text-xs uppercase tracking-wider">Offices</p>
                          </div>
                          <div className="text-center">
                            <p className="font-display text-xl text-gold">{partner.countries}</p>
                            <p className="font-body text-cream/60 text-xs uppercase tracking-wider">Countries</p>
                          </div>
                        </div>
                      </div>

                      {/* Content Section */}
                      <div className="flex-1">
                        <h2 className="font-display text-2xl md:text-3xl lg:text-4xl text-cream mb-4">
                          {partner.name}
                        </h2>
                        <p className="font-body text-cream/80 text-lg leading-relaxed mb-6">
                          {partner.description}
                        </p>
                        <div className="bg-white/5 rounded-lg p-4 mb-6">
                          <p className="font-body text-sm text-gold uppercase tracking-wider mb-1">Specialty</p>
                          <p className="font-body text-cream/90">{partner.specialty}</p>
                        </div>
                        <Button
                          variant="outline"
                          className="border-cream/30 text-cream hover:bg-cream/10"
                          asChild
                        >
                          <a href={partner.website} target="_blank" rel="noopener noreferrer">
                            Visit {partner.shortName}
                            <ExternalLink className="ml-2" size={16} />
                          </a>
                        </Button>
                      </div>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* Collaboration CTA */}
        <section className="py-20 bg-charcoal">
          <div className="container mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-center max-w-3xl mx-auto"
            >
              <h2 className="font-display text-3xl md:text-4xl text-cream mb-6">
                The Power of Partnership
              </h2>
              <p className="font-body text-cream/70 text-lg mb-8">
                Through our exclusive partnerships with these world-renowned agencies, Agota Estate 
                provides access to an unmatched portfolio of luxury properties across the globe. 
                Whether you're seeking a Mediterranean villa, a Dubai penthouse, or a historic 
                European estate, our network ensures you'll find your perfect home.
              </p>
              <Button variant="luxury" size="lg" asChild>
                <a href="/#contact">Explore Properties Together</a>
              </Button>
            </motion.div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
}
